import { useCallback } from 'react';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import seoTasksService from '@/services/seoTasks';
import wordpressService from '@/services/wordpress';
import { SeoTask, SeoProject } from '@/types/api/seo';

export function useKeywordTasks(
  keywordId: string | undefined,
  project: SeoProject | undefined,
  targetPageUrl: string,
  latestSnapshotUrl?: string
) {
  const {
    data: tasks = [],
    isLoading,
    refetch,
  } = useSeoFetch<SeoTask[]>(
    ['seo-keyword-tasks', keywordId],
    () => seoTasksService.getByKeyword(keywordId as string),
    { enabled: !!keywordId }
  );

  const handleStatusChange = useCallback(
    async (taskId: string, status: SeoTask['status']) => {
      try {
        if (status === 'dismissed') {
          await seoTasksService.delete(taskId);
        } else {
          await seoTasksService.updateStatus(taskId, status);
        }
        refetch();
      } catch {
        /* ignore */
      }
    },
    [refetch]
  );

  const handleApplyToWp = useCallback(
    async (task: SeoTask) => {
      if (!project || !task.suggestedValue) return;
      try {
        const changes: Record<string, string> = {};
        if (task.taskType === 'title_tag') changes.seoTitle = task.suggestedValue;
        else if (task.taskType === 'meta_description') changes.seoDescription = task.suggestedValue;
        else if (task.taskType === 'content') changes.content = task.suggestedValue;
        else if (task.taskType === 'schema') changes.schema = task.suggestedValue;

        const pageUrl = targetPageUrl || latestSnapshotUrl || '';
        if (pageUrl) {
          const wpPost = await wordpressService.findPostByUrl(project.id, pageUrl);
          if (wpPost) {
            await wordpressService.applyChanges({
              projectId: project.id,
              postId: wpPost.postId,
              postType: wpPost.postType,
              keywordId: task.keywordId ?? undefined,
              pageUrl,
              changes,
            });
            await seoTasksService.markAppliedToWp(task.id);
            refetch();
          }
        }
      } catch {
        /* ignore */
      }
    },
    [project, targetPageUrl, latestSnapshotUrl, refetch]
  );

  return { tasks, isLoading, refetch, handleStatusChange, handleApplyToWp };
}
